import { useMutation, useSuspenseQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";

import { Section, SectionHeader } from "@roqua/components/sections";
import { Badge } from "@roqua/components/ui/badge";

import { PageTitle } from "@/components/PageTitle";
import { SolicitFeedback } from "@/components/SolicitFeedback";
import { BackButton } from "@/components/ui/BackButton";
import { ButtonWithConfirmation } from "@/components/ui/button-with-confirmation";
import { Details } from "@/features/ProtocolSubscriptions/Show/Details";
import { ProtocolSubscriptionShow } from "@/features/ProtocolSubscriptions/Show/query";
import { SubscriptionTimeline } from "@/features/ProtocolSubscriptions/Show/SubscriptionTimeline";
import { gql } from "@/generated/gql";
import { useTranslation } from "@/i18n/client";
import { isActive } from "@/models/protocol_subscription";
import { useSessionContext } from "@/session";

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/protocol_subscriptions/show/$protocolSubscriptionId",
)({
  component: RouteComponent,
});

const StopMutation = gql(`
  mutation ProtocolSubscriptionShowStop($id: ID!) {
    protocolSubscriptionStop(input: {protocolSubscriptionId: $id}) {
      errors {
        path
      }
    }
  }
`);

function RouteComponent() {
  const { t } = useTranslation(["ProtocolSubscriptions", "common"]);
  const { dossierId, protocolSubscriptionId } = Route.useParams();
  const { legacyRoutes } = useSessionContext();
  const { data } = useSuspenseQuery(ProtocolSubscriptionShow, {
    variables: { dossierId, protocolSubscriptionId },
  });
  const [stop] = useMutation(StopMutation, {
    variables: { id: protocolSubscriptionId },
    refetchQueries: [ProtocolSubscriptionShow],
    awaitRefetchQueries: true,
  });
  const subscription = data.dossier.protocolSubscription;

  return (
    <>
      <PageTitle title="protocolSubscriptionsShow" />
      <Section variant="header">
        <SectionHeader
          header={subscription.autoProtocol.name}
          buttons={
            <>
              <SolicitFeedback />
              <BackButton />
              {isActive(subscription.status) && (
                <ButtonWithConfirmation
                  variant="destructive"
                  className="rounded-full"
                  confirmDescription={t("show.stopConfirmation")}
                  onConfirm={async () => {
                    await stop();
                  }}
                >
                  {t("stop")}
                </ButtonWithConfirmation>
              )}
            </>
          }
        />
        <div className="flex items-center gap-2">
          <Badge>{t(`common:respondent.type.${subscription.respondentType}`)}</Badge>
          <Badge variant="outline" className="text-white">
            {t(`show.status.${subscription.status}`)}
          </Badge>
          <a
            href={legacyRoutes.protocolSubscriptionUrl(subscription.id)}
            className="ms-auto text-sm underline"
          >
            {t("show.oldVersion")}
          </a>
        </div>
      </Section>

      <Section variant="middle">
        <Details subscription={subscription} />
      </Section>

      <Section>
        <SectionHeader header={t("show.timeline.header")} />
        <SubscriptionTimeline subscription={subscription} />
      </Section>
    </>
  );
}
